import { supabase } from '../lib/supabase'
import { 
  AircraftMovement, 
  AircraftMovementInput, 
  TemporaryAircraftState, 
  ContainerType,
  UndoableMovement 
} from '../types/database'
import { Aircraft } from '../types/intellijetnet'
import { AircraftTransactionsService } from './aircraft-transactions.service'
import { OverridesService } from './overrides.service'
import { OffMarketService } from './off-market.service'
import { AircraftMovementValidator, MovementValidationContext, MovementValidationResult } from '../utils/aircraft-movement-validation'
import { MovementAuditService } from './movement-audit.service'

export class AircraftMovementService {
  private static readonly MOVEMENTS_TABLE = 'aircraft_movements'
  private static readonly TEMP_STATES_TABLE = 'temporary_aircraft_states'

  /**
   * Validate if an aircraft can be moved between containers
   */
  static async validateMovement(
    aircraft: Aircraft,
    fromContainer: ContainerType,
    toContainer: ContainerType,
    additionalContext?: Partial<MovementValidationContext>
  ): Promise<MovementValidationResult> {
    try {
      // Get recent movements for this aircraft
      const recentMovements = await this.getRecentMovements(aircraft.aircraft_id)

      // Get override data (for future use in movement validation)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _overrideData = await OverridesService.getByAircraftId(aircraft.aircraft_id)

      // Check if aircraft is in off-market system
      let offMarketRecord = null
      try {
        const offMarketData = await OffMarketService.getByAircraftId(aircraft.aircraft_id)
        offMarketRecord = offMarketData.length > 0 ? offMarketData[0] : null
      } catch (error) {
        // Could not check off-market status
      }
      
      // Get last snapshot date if moving from sold
      let lastSnapshotDate: string | null = null
      if (fromContainer === 'sold') {
        const snapshots = await AircraftTransactionsService.getByAircraftId(aircraft.aircraft_id)
        if (snapshots.length > 0) {
          lastSnapshotDate = snapshots[0].transaction_date
        }
      }

      const context: MovementValidationContext = {
        aircraft,
        fromContainer,
        toContainer,
        recentMovements,
        lastSnapshotDate,
        offMarketRecord: offMarketRecord,
        ...additionalContext
      }

      return AircraftMovementValidator.validateMovement(context)
    } catch (error) {
      return {
        isValid: false,
        reason: 'Failed to validate movement due to system error'
      }
    }
  }

  /**
   * Move an aircraft between containers
   */
  static async moveAircraft(
    aircraft: Aircraft,
    fromContainer: ContainerType,
    toContainer: ContainerType,
    options: {
      reason?: string
      notes?: string
      movedBy?: string
      skipValidation?: boolean
    } = {}
  ): Promise<{ movement: AircraftMovement; tempState?: TemporaryAircraftState }> {
    try {
      // Validate movement unless skipped
      if (!options.skipValidation) {
        const validation = await this.validateMovement(aircraft, fromContainer, toContainer)
        if (!validation.isValid) {
          throw new Error(validation.reason || 'Movement validation failed')
        }
      }

      // Create movement record
      const movementInput: AircraftMovementInput = {
        aircraft_id: aircraft.aircraft_id,
        model_id: aircraft.model_id || 0,
        from_container: fromContainer,
        to_container: toContainer,
        movement_reason: options.reason,
        moved_by: options.movedBy,
        notes: options.notes
      }

      const movement = await this.createMovement(movementInput)

      // Handle container-specific logic
      let tempState: TemporaryAircraftState | undefined
      let snapshotId: string | null = null

      // If moving TO sold, create transaction snapshot
      if (toContainer === 'sold') {
        try {
          snapshotId = await this.createTransactionSnapshot(aircraft, movement.movement_id)
          await this.updateMovementSnapshotReference(movement.movement_id, 'created_snapshot_id', snapshotId)
        } catch (error) {
          // Failed to create transaction snapshot - allow movement to continue without snapshot
          snapshotId = null
        }
      }

      // If moving FROM sold, delete transaction snapshot (if allowed)
      if (fromContainer === 'sold') {
        const deletedSnapshotId = await this.deleteTransactionSnapshot(aircraft.aircraft_id)
        if (deletedSnapshotId) {
          await this.updateMovementSnapshotReference(movement.movement_id, 'deleted_snapshot_id', deletedSnapshotId)
        }
      }

      // Create temporary state for the aircraft in new container
      tempState = await this.createTemporaryState(movement, aircraft, toContainer)

      // Log the movement action to audit trail
      await MovementAuditService.logMovementAction(movement, 'move', {
        userId: options.movedBy,
        userName: options.movedBy,
        reason: options.reason,
        notes: options.notes,
        validationPassed: true
      })



      return { movement, tempState }
    } catch (error) {
      throw error
    }
  }

  /**
   * Undo a movement (if still within time limit)
   */
  static async undoMovement(movementId: string, undoneBy?: string): Promise<void> {
    try {
      // Check if movement can be undone
      const movement = await this.getMovementById(movementId)
      if (!movement) {
        throw new Error('Movement not found')
      }

      if (movement.status !== 'pending') {
        throw new Error('Movement cannot be undone - status is not pending')
      }

      if (new Date(movement.expires_at) <= new Date()) {
        throw new Error('Movement cannot be undone - time limit expired')
      }

      // Update movement status
      await this.updateMovementStatus(movementId, 'undone', undoneBy)

      // Deactivate temporary state
      await this.deactivateTemporaryState(movementId)

      // Handle snapshot cleanup
      if (movement.created_snapshot_id) {
        // Delete the snapshot that was created
        await AircraftTransactionsService.delete(movement.created_snapshot_id)
      }

      if (movement.deleted_snapshot_id) {
        // Restore the snapshot that was deleted (this is complex - might need to recreate)
        // Restoring deleted snapshot not yet implemented
      }

      // Log the undo action to audit trail
      await MovementAuditService.logMovementAction(movement, 'undo', {
        userId: undoneBy,
        userName: undoneBy
      })


    } catch (error) {
      throw error
    }
  }

  /**
   * Get recent movements for an aircraft
   */
  static async getRecentMovements(aircraftId: number, days: number = 30): Promise<AircraftMovement[]> {
    try {
      const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()

      const { data, error } = await supabase
        .from(this.MOVEMENTS_TABLE)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .gte('moved_at', cutoffDate)
        .order('moved_at', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch recent movements: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all undoable movements for a model
   */
  static async getUndoableMovements(modelId: number): Promise<UndoableMovement[]> {
    try {
      const { data, error } = await supabase
        .from('undoable_movements')
        .select('*')
        .eq('model_id', modelId)
        .order('moved_at', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch undoable movements: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get temporary aircraft states for a container
   */
  static async getTemporaryStates(modelId: number, container: ContainerType): Promise<TemporaryAircraftState[]> {
    try {
      const { data, error } = await supabase
        .from(this.TEMP_STATES_TABLE)
        .select('*')
        .eq('model_id', modelId)
        .eq('container', container)
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (error) {
        throw new Error(`Failed to fetch temporary states: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  // Private helper methods

  private static async createMovement(input: AircraftMovementInput): Promise<AircraftMovement> {
    const { data, error } = await supabase
      .from(this.MOVEMENTS_TABLE)
      .insert(input)
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create movement: ${error.message}`)
    }

    return data
  }

  private static async getMovementById(movementId: string): Promise<AircraftMovement | null> {
    const { data, error } = await supabase
      .from(this.MOVEMENTS_TABLE)
      .select('*')
      .eq('movement_id', movementId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch movement: ${error.message}`)
    }

    return data
  }

  private static async updateMovementStatus(movementId: string, status: string, undoneBy?: string): Promise<void> {
    const updates: any = { status }
    if (status === 'undone' && undoneBy) {
      updates.undone_by = undoneBy
      updates.undone_at = new Date().toISOString()
    }

    const { error } = await supabase
      .from(this.MOVEMENTS_TABLE)
      .update(updates)
      .eq('movement_id', movementId)

    if (error) {
      throw new Error(`Failed to update movement status: ${error.message}`)
    }
  }

  private static async updateMovementSnapshotReference(
    movementId: string, 
    field: 'created_snapshot_id' | 'deleted_snapshot_id', 
    snapshotId: string
  ): Promise<void> {
    const { error } = await supabase
      .from(this.MOVEMENTS_TABLE)
      .update({ [field]: snapshotId })
      .eq('movement_id', movementId)

    if (error) {
      throw new Error(`Failed to update movement snapshot reference: ${error.message}`)
    }
  }

  private static async createTransactionSnapshot(aircraft: Aircraft, movementId: string): Promise<string> {
    // Get comprehensive override data and options for the aircraft
    const overrideData = await OverridesService.getByAircraftId(aircraft.aircraft_id)

    // Get aircraft options
    let aircraftOptions: string[] = []
    try {
      const { data: aircraftRecord } = await supabase
        .from('aircraft')
        .select('options')
        .eq('aircraft_id', aircraft.aircraft_id)
        .maybeSingle()

      aircraftOptions = aircraftRecord?.options || []
    } catch (error) {
      // Could not fetch aircraft options for snapshot
    }

    // Helper function to truncate strings to fit database constraints
    const truncateString = (value: string | null | undefined, maxLength: number): string | null => {
      if (!value) return null
      return value.length > maxLength ? value.substring(0, maxLength) : value
    }

    // Create comprehensive snapshot with all override data
    const snapshotData = {
      aircraft_id: aircraft.aircraft_id,
      model_id: aircraft.model_id || 0,
      transaction_date: new Date().toISOString().split('T')[0],
      sale_price: null, // Will be filled in later when user sets sold price
      transaction_notes: `Manual move to sold container - Movement ID: ${movementId}`,

      // Basic aircraft data with safe truncation
      sernbr: truncateString(aircraft.sernbr, 50),
      regnbr: truncateString(aircraft.regnbr, 20),
      make: truncateString(aircraft.make, 100),
      model: truncateString(aircraft.model, 100),
      yeardlv: aircraft.yeardlv,

      // Capture all override data or fall back to API data
      asking_price: overrideData?.asking_price,
      take_price: overrideData?.take_price,
      external_asking_price: overrideData?.external_asking_price,
      external_take_price: overrideData?.external_take_price,
      external_sale_price: null, // Will be set when user enters sold price

      aftt: overrideData?.aftt ?? aircraft.aftt,
      tbo: overrideData?.tbo,
      landings: overrideData?.landings,
      pax: overrideData?.pax,

      base_airport: truncateString(overrideData?.base_airport ?? aircraft.baseairport, 10),
      base_city: truncateString(overrideData?.base_city ?? aircraft.basecity, 100),
      base_state: truncateString(overrideData?.base_state ?? aircraft.basestate, 50),
      base_country: truncateString(overrideData?.base_country ?? aircraft.basecountry, 50),

      damage_history: truncateString(overrideData?.damage_history, 20),
      emp: truncateString(overrideData?.emp, 100),
      status: truncateString(overrideData?.status, 50),
      maintenance_program: truncateString(overrideData?.maintenance_program, 100),

      for_sale: overrideData?.for_sale,
      leased: overrideData?.leased,
      date_listed: overrideData?.date_listed,
      date_purchased: overrideData?.date_purchased,

      // Owner/Broker data
      owner: overrideData?.owner,
      broker: overrideData?.broker,
      owner_custom: overrideData?.owner_custom,
      broker_custom: overrideData?.broker_custom,

      // Custom columns
      custom_1_value: overrideData?.custom_1_value,
      custom_2_value: overrideData?.custom_2_value,
      custom_3_value: overrideData?.custom_3_value,

      // Options snapshot
      options: aircraftOptions
    }

    const snapshot = await AircraftTransactionsService.create(snapshotData)
    return snapshot.transaction_id
  }

  private static async deleteTransactionSnapshot(aircraftId: number): Promise<string | null> {
    // Find and delete the most recent snapshot for this aircraft
    const snapshots = await AircraftTransactionsService.getByAircraftId(aircraftId)
    if (snapshots.length === 0) return null

    const latestSnapshot = snapshots[0]
    await AircraftTransactionsService.delete(latestSnapshot.transaction_id)
    return latestSnapshot.transaction_id
  }

  private static async createTemporaryState(
    movement: AircraftMovement, 
    aircraft: Aircraft, 
    container: ContainerType
  ): Promise<TemporaryAircraftState> {
    const overrideData = await OverridesService.getByAircraftId(aircraft.aircraft_id)

    const stateData = {
      movement_id: movement.movement_id,
      aircraft_id: aircraft.aircraft_id,
      model_id: aircraft.model_id || 0,
      container,
      aircraft_data: aircraft,
      override_data: overrideData,
      display_order: 0,
      is_active: true
    }

    const { data, error } = await supabase
      .from(this.TEMP_STATES_TABLE)
      .insert(stateData)
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create temporary state: ${error.message}`)
    }

    return data
  }

  private static async deactivateTemporaryState(movementId: string): Promise<void> {
    const { error } = await supabase
      .from(this.TEMP_STATES_TABLE)
      .update({ is_active: false })
      .eq('movement_id', movementId)

    if (error) {
      throw new Error(`Failed to deactivate temporary state: ${error.message}`)
    }
  }
}
